import { getUserPresentableMessage } from "@core/foundation/error";
import { noop } from "@core/foundation/function";
import type { LoggerContract } from "@core/foundation/logger";
import type { Promisable } from "@core/foundation/promise";
import { hasValue, isNullish } from "@core/foundation/types";

import type { AsyncStatusStore, SessionStore } from "../../zustand/index.ts";
import type { AuthStore } from "../state/auth-store.ts";

interface SigninOnReturnHandlerConfig {
  readonly appCallbackUrl?: string;
  readonly authSessionStore: SessionStore;
  readonly authStore: AuthStore;
  readonly getAuthToken: (params: {
    readonly code: string;
  }) => Promisable<{ readonly authToken: string }>;
  readonly homeUrl?: string;
  readonly isValidToken: (token: string) => boolean;
  readonly logger: LoggerContract;
  readonly onSignin?: (params: {
    readonly authToken: string;
  }) => Promisable<void>;
  readonly signinOnReturnStore: AsyncStatusStore;
  readonly webCallbackUrl: string;
}

type SigninOnReturnHandler = (url: string) => Promisable<void>;

export function createSigninOnReturnHandler(
  config: SigninOnReturnHandlerConfig,
): SigninOnReturnHandler {
  const {
    appCallbackUrl,
    authSessionStore,
    authStore,
    getAuthToken,
    homeUrl,
    isValidToken,
    logger,
    onSignin = noop,
    signinOnReturnStore,
    webCallbackUrl,
  } = config;

  return async (url: string): Promise<void> => {
    if (hasValue(homeUrl) && window.location.pathname === webCallbackUrl) {
      window.history.replaceState(null, "", homeUrl);
    }

    const urlObj = new URL(url);

    const isWebCallback = urlObj.pathname === webCallbackUrl;

    const isAppCallback = hasValue(appCallbackUrl)
      ? matchesAppCallback(urlObj, appCallbackUrl)
      : false;

    if ((!isWebCallback && !isAppCallback) || signinOnReturnStore.isPending) {
      return;
    }

    const token = urlObj.searchParams.get("token");

    if (hasValue(token)) {
      if (!isValidToken(token)) {
        logger.warn("Invalid user token format");
        signinOnReturnStore.setError("Invalid token");

        return;
      }

      await onSignin({ authToken: token });
      authStore.setToken(token);

      return;
    }

    const code = urlObj.searchParams.get("code");

    const session = urlObj.searchParams.get("session");

    if (isNullish(code) || isNullish(session)) {
      return;
    }

    if (isNullish(authSessionStore.session)) {
      logger.warn("Session not found");
      signinOnReturnStore.setError("Session not found");

      return;
    }

    if (session !== authSessionStore.session) {
      logger.warn("Session mismatch");
      signinOnReturnStore.setError("Session mismatch");

      return;
    }

    if (authSessionStore.isCodeRedeemed(code)) {
      logger.warn("Code already redeemed");
      signinOnReturnStore.setError("Code already redeemed");

      return;
    }

    signinOnReturnStore.setIsPending(true);

    try {
      const { authToken } = await getAuthToken({ code });
      await onSignin({ authToken });
      authStore.setToken(authToken);
      authSessionStore.redeemCode(code);
      authSessionStore.endSession();
    } catch (error) {
      logger.error("Failed to sign in", error);

      signinOnReturnStore.setError(
        getUserPresentableMessage(error, "Failed to sign in"),
      );
    } finally {
      signinOnReturnStore.setIsPending(false);
    }
  };
}

function matchesAppCallback(urlObj: URL, appCallbackUrl: string): boolean {
  const callbackUrl = new URL(appCallbackUrl);

  return (
    urlObj.origin === callbackUrl.origin &&
    urlObj.pathname === callbackUrl.pathname
  );
}
